<template>
  <base-dialog-button :isOpen="dialogOpen" @closeDialog="toggleDialog" :size="mobileVersion ? 'fs' : 'lg-fix-h'">
    <template slot="header">
      <div class="h-16 bg-secondary flex items-center justify-between text-white px-4">
        <div class="flex items-center">
          <h2 class="font-bold ml-2">{{ header }}</h2>
        </div>
        <span class="cursor-pointer" @click="toggleDialog">
          <base-button-icon name="cross" size="xs" iconSize="sm" mode="secondary" />
        </span>
      </div>
    </template>
    <template slot="main">
      <div class="form-wrapper p-4 overflow-x-auto"
        :style="mobileVersion ? 'height: 80vh;' : 'height: 60vh;'">
        <el-form ref="ruleForm" class="h-full" :model="form" :rules="rules">
          <div class="lg:mb-2 flex gap-2" :class="{ '!block': !isLower1650 }">
            <div class="w-full" :class="{ 'mb-2': !isLower1650 }">
              <el-form-item prop="invoiceSerial" class="mb-0 pb-4">
                <label
                  class="text-sm font-semibold text-secondary-600 block mb-1"><base-obligatory-icon v-if="toProforma" />{{ $t('invoices.serie_factura') }}</label>
                <base-el-select v-model="form.invoiceSerial" :options="seriesFactura" :loading="isLoadingSeries" :disabled="loading || toProforma"
                  allow-create clearable filterable :exclude-falsys="['']" :option-label="serieFacturaLabel" />
              </el-form-item>
            </div>
            <div class="w-full">
              <el-form-item prop="invoiceNumber" class="mb-0 pb-4">
                <label
                  class="text-sm font-semibold text-secondary-600 block mb-1"><base-obligatory-icon />{{ $t('invoices.num_factura') }}</label>
                <el-input v-model="form.invoiceNumber" :disabled="true" />
              </el-form-item>
            </div>
          </div>
          <div class="lg:mb-2">
            <div class="w-full">
              <el-form-item prop="issueDate" class="mb-0 pb-4">
                <label
                  class="text-sm font-semibold text-secondary-600 block mb-1"><base-obligatory-icon />{{ $t('invoices.fecha_emision') }}</label>
                <el-date-picker v-model="form.issueDate" type="date" :picker-options="dateRangeOptions"
                  popper-class="!z-[2020]" :format="datePattern" value-format="yyyy-MM-dd[T00:00:00]" :disabled="loading" />
              </el-form-item>
            </div>
          </div>
          <div v-if="!toProforma" class="lg:mb-2">
            <div class="w-full">
              <el-switch class="switch-text break-normal" v-model="form.autoSend" :active-text="$t('app.send_auto')" :disabled="loading"></el-switch>
            </div>
          </div>
        </el-form>
      </div>
    </template>
    <template slot="footer">
      <div class="flex flex-row w-full justify-between">
        <base-button class="w-full mx-2" mode="secondary-phantom-blue" :text="$t('app.cancel')" :loading="loading" :disabled="isGeneralLoading" @click="toggleDialog" />
        <base-button mode="secondary-phantom-blue" class="w-full mx-2" :loading="loading" :disabled="isGeneralLoading"
          :text="applyText" @click="handlerApply('ruleForm')" />
      </div>
    </template>
  </base-dialog-button>
</template>

<script>
import moment from 'moment'
import { bus } from '@/main'
import { i18n } from '@/i18n'
import { getInvoice } from '@/services/invoices/getInvoice'
import getLocaleDateTimeFormat from '@/services/functions/getLocaleDateTimeFormat'
import { facturarProformaPresupuesto } from '@/services/invoices/facturarProformaPresupuesto'
import { updateProformaPresupuesto } from '@/services/invoices/updateProformaPresupuesto'
import { getNextInvoiceDefault } from '@/services/configurations/getNextInvoiceDefault'
import { getNextInvoicePresupuestoProforma } from '@/services/configurations/getNextInvoicePresupuestoProforma'
import { getSeries } from '@/services/configurations/getSeries'

export default {
  name: 'to-invoice-proforma-dialog',
  props: {
    dialogOpen: {
      type: Boolean,
      default: false
    },
    mobileVersion: {
      type: Boolean,
      default: false
    },
    idFacturae: [Number, String],
    idInvoice: [Number, String],
    invoice: {
      type: Object,
      default: null
    },
    toProforma: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      dateRangeOptions: {
        firstDayOfWeek: 1,
      },
      loading: false,
      isLoadingSeries: false,
      isLoadingInvoiceNumber: false,
      seriesFactura: [],
      isOpen: this.dialogOpen,
      files: [],
      form: this.getDefaultForm(),
      status: null,
      rules: {
        invoiceNumber: [{
          required: true,
          message: i18n.t('validation.complete'),
          trigger: ['blur', 'change'],
        }],
        invoiceSerial: [
          {
            required: this.toProforma,
            message: i18n.t('validation.complete'),
            trigger: ['blur', 'change'],
          },
          {
            trigger: ['blur', 'change'],
            validator: (rule, value, callback) => {
              if (this.toProforma) {
                if (value.toUpperCase() !== 'PROFORMA') {
                  return callback(new Error(i18n.t('validation.serie_factura_error')))
                }
              } else if (['PROFORMA', 'PRESUPUESTO'].includes(value.toUpperCase())) {
                return callback(new Error(i18n.t('validation.serie_factura_error')))
              }
              callback()
            }
          },
        ],
        issueDate: [{
          required: true,
          message: i18n.t('validation.complete'),
          trigger: ['blur', 'change'],
        }]
      },
    }
  },
  computed: {
    datePattern () {
      const formatKey = 'numeric_date'
      const locale = i18n.locale || 'es'
      return getLocaleDateTimeFormat(formatKey, locale)
    },
    isLower1650: function () {
      return this.$store.getters['app/getIsLower1650']
    },
    isGeneralLoading: function () {
      return this.loading || this.isLoadingSeries || this.isLoadingInvoiceNumber
    },
    header: function () {
      if (this.toProforma) {
        return i18n.t('invoices.dialogs.quotation_to_proforma_header')
      }
      if (this.status === 28) {
        return i18n.t('invoices.dialogs.invoice_proforma_header')
      }
      return i18n.t('invoices.dialogs.invoice_quotation_header')
    },
    applyText: function () {
      if (this.toProforma) {
        return i18n.t('tipos.accion.convert_to_proforma')
      }
      return i18n.t('tipos.accion.convert_to_invoice')
    }
  },
  watch: {
    // Inicializar al abrir el componente
    dialogOpen: async function (value, oldValue) {
      if (value) {
        this.resetForm()
        var invoice = this.invoice || null
        if (invoice === null) {
          invoice = await this.downloadInvoiceDataMetadata()
        }
        this.setInvoice(invoice)
        const idCIFEmpresa = this.form.idCIFEmpresa || -1
        await this.downloadInvoiceSeries(idCIFEmpresa)
        await this.getInvoiceSerialNumber(idCIFEmpresa, this.form.invoiceSerial)
      }
      this.isOpen = !!value
    },
    'form.invoiceSerial': async function (value, oldValue) {
      if (value !== oldValue && this.isOpen) {
        await this.getInvoiceSerialNumber(this.form.idCIFEmpresa || -1, value)
      }
    },
    $route (to, from) {
      if (to !== from) {
        this.status = to.meta.status
      }
    },
  },
  methods: {
    getDefaultForm: function () {
      return {
        idFacturae: '',
        idInvoice: '',
        idCIFEmpresa: '',
        invoiceNumber: '',
        invoiceSerial: '',
        issueDate: '',
        autoSend: false,
      }
    },
    resetForm: function () {
      this.form = this.getDefaultForm()
    },
    setInvoice: function (invoice) {
      if (invoice) {
        this.form.idFacturae = invoice.idFacturae
        this.form.idInvoice = invoice.idInvoice
        this.form.idCIFEmpresa = invoice.idCIFEmpresa
        this.form.invoiceNumber = invoice.NumFactura || ''
        this.form.issueDate = invoice.FechaFactura || moment().format('yyyy-MM-DD[T00:00:00]')
        this.form.invoiceSerial = this.toProforma ? 'PROFORMA' : ''
      }
    },
    toggleDialog: function () {
      if (!this.loading) {
        this.resetForm()
        this.$emit('toggleDialog')
      }
    },
    handlerApply: async function (formName) {
      if (!this.toProforma) { 
        await this.facturarProforma(formName) 
      } else {
        await this.updateProforma(formName)
      }
    },
    facturarProforma: async function (formName) {
      if (!this.loading) {
        this.$refs[formName].validate(async (valid) => {
          if (valid) {
            try {
              this.loading = true
              const payload = {
                idFacturae: this.form.idFacturae || null,
                idInvoice: this.form.idInvoice || null,
                invoiceSeriesNumber: this.form.invoiceSerial || null,
                invoiceNumber: this.form.invoiceNumber || null,
                invoiceIssueDate: this.form.issueDate || null,
                autoSend: this.form.autoSend 
              }
              const response = await facturarProformaPresupuesto(payload)
              if (response.blnError) {
                this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
              } else {
                this.doFeedback(i18n.t('feedback.success.entity_updated_msg'), 'success')
                this.$emit('applyDialog')
                bus.$emit('changeIt')
              }
            } finally {
              this.loading = false
            }
          }
        })
      }
    },
    updateProforma: async function (formName) {
      if (!this.loading) {
        this.$refs[formName].validate(async (valid) => {
          if (valid) {
            try {
              this.loading = true
              const payload = {
                proformaPresupuestosSeries: this.form.invoiceSerial || null,
                proformaPresupuestosNumber: this.form.invoiceNumber || null,
                proformaPresupuestosIssueDate: this.form.issueDate || null,
              }
              const params = {
                idFacturae: this.form.idFacturae || null,
                idInvoice: this.form.idInvoice || null,
              }
              const response = await updateProformaPresupuesto(payload, params)
              if (response.blnError) {
                this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
              } else {
                this.doFeedback(i18n.t('feedback.success.entity_updated_msg'), 'success')
                this.$emit('applyDialog')
                bus.$emit('changeIt')
              }
            } finally {
              this.loading = false
            }
          }
        })
      }
    },
    downloadInvoiceSeries: async function (idCIFEmpresa) {
      try {
        this.isLoadingSeries = true
        const response = await getSeries({ idCIFEmpresa })
        if (response.blnError) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', response))
          return
        }
        const series = response.Respuesta || []
        if (this.toProforma) series.unshift('PROFORMA')
        if (!series.includes('')) series.unshift('')
        this.seriesFactura = series
      } finally {
        this.isLoadingSeries = false
      }
    },
    getInvoiceSerialNumber: async function (idCIFEmpresa, serieFactura = '') {
      try {
        this.isLoadingInvoiceNumber = true
        var data = null
        if (this.toProforma) {
          const params = { idCIFEmpresa, serieFactura, suggestionFor: 'PROFORMA' }
          data = await getNextInvoicePresupuestoProforma(params)
        } else {
          const params = { idCIFEmpresa, serieFactura }
          data = await getNextInvoiceDefault(params)
        }
        if (data.blnError === true) {
          const feedbackType = data.codError === 'GNID004' ? 'warning' : undefined
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', data), feedbackType)
        }
        this.form.invoiceNumber = data.respuesta?.InvoiceNumber || ''
      } finally {
        this.isLoadingInvoiceNumber = false
      }
    },
    serieFacturaLabel: function (option) {
      var entity = i18n.t('invoices.serie_factura_short')
      if (['de', 'eu'].includes(i18n.locale)) {
        entity = entity.toLowerCase()
      }
      return option || `(${i18n.t('app.select_entity_or_write_new_placeholder_long_f', { entity })})`
    },
    downloadInvoiceDataMetadata: async function () {
      try {
        this.loading = true
        const params = {
          idFacturae: this.idFacturae || null,
          idInvoice: this.idInvoice || null,
        }
        const invoice = await getInvoice(params)
        if (invoice.blnError) {
          this.doFeedback(i18n.t('feedback.error.formated_api_response_error', invoice))
        }
        return invoice.respuesta
      } catch (error) {
        this.doFeedback(i18n.t('feedback.error.general'))
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
<style scoped>

</style>
